import { StackProps } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Contact, EarliestTenderOption, TimeOfDay } from '@pelicargo/types';
import { modelUtils } from '@pelicargo/utils';
import { omit } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { address } from '../hooks/quote/useInstructionForm';
import { useUpdateQuote } from '../hooks/quote/useUpdate';
import { useRequest } from '../hooks/request/useRequest';
import { useUpdateRequest } from '../hooks/request/useUpdate';
import { Quote as BaseQuote, Request } from '../utils/types';
import { AfrBookingForm } from './Forms/AfrBookingForm';
import { ExwBookingForm } from './Forms/ExwBookingForm';
import { FcaBookingForm } from './Forms/FcaBookingForm';
import { SimpleForm } from './SimpleForm';

const validationSchema = z.object({
  awb_number: z.string().nullish(),
  additional_information: z.string().nullish(),
  booking_instructions: z.string().nullish(),
  cutoff_at: z.coerce.date().nullish(),
  dropoff_instructions: z.string().nullish(),
  delivery_address: address.nullish(),

  customer_ref: z.string().nullish(),
  pickup_ref: z.string().nullish(),
  trucking_ref: z.string().nullish(),

  tender_date: z.coerce.date().nullish(),
  tender_date_time_of_day: z.nativeEnum(TimeOfDay).nullish(),
  earliest_tender: z.nativeEnum(EarliestTenderOption).nullish(),
  dropoff_contact_id: z.coerce.number().nullish(),
  inland_dropoff_contact_id: z.coerce.number().nullish(),
  from_dock_hours: z.coerce.number().nullish(),
  to_dock_hours: z.coerce.number().nullish(),
  pickup_ready_at: z.coerce.date().nullish(),

  emergency_contact_id: z.coerce.number().nullish(),
});

type Quote = BaseQuote & { dropoff_contact_id: Contact; inland_dropoff_contact_id?: Contact };

type BookingTemplateFormProps = StackProps & {
  quote: Quote;
  request?: Partial<Request>;
};

const REQUEST_KEYS: (keyof RequestKeys)[] = ['customer_ref'];
const QUOTE_KEYS: (keyof QuoteKeys)[] = [
  'additional_information',
  'booking_instructions',
  'dropoff_instructions',
  'awb_instructions',
  'delivery_address',
  'tender_date',
  'tender_date_time_of_day',
  'earliest_tender',
  'cutoff_at',
  'dropoff_contact_id',
  'inland_dropoff_contact_id',
  'awb_number',
  'provider_id',
  'from_dock_hours',
  'to_dock_hours',
  'pickup_ready_at',
  'dropoff_at',
  'pickup_ref',
  'trucking_ref',
  'emergency_contact_id',
];

type RequestKeys = Pick<Request, 'customer_ref'>;
type QuoteKeys = Pick<
  Quote,
  | 'additional_information'
  | 'booking_instructions'
  | 'dropoff_instructions'
  | 'awb_instructions'
  | 'delivery_address'
  | 'tender_date'
  | 'tender_date_time_of_day'
  | 'earliest_tender'
  | 'cutoff_at'
  | 'dropoff_contact_id'
  | 'inland_dropoff_contact_id'
  | 'awb_number'
  | 'provider_id'
  | 'from_dock_hours'
  | 'to_dock_hours'
  | 'pickup_ready_at'
  | 'dropoff_at'
  | 'pickup_ref'
  | 'trucking_ref'
  | 'emergency_contact_id'
>;

type Values = RequestKeys & QuoteKeys;

const mergeValues = (request?: Partial<Request>, quote?: Partial<Quote>): Partial<Values> => ({
  awb_number: quote?.awb_number,
  additional_information: quote?.additional_information,
  booking_instructions: quote?.booking_instructions,
  dropoff_instructions: quote?.dropoff_instructions,
  awb_instructions: quote?.awb_instructions,
  delivery_address: quote?.delivery_address,
  customer_ref: request?.customer_ref,
  pickup_ref: quote?.pickup_ref,
  trucking_ref: quote?.trucking_ref,
  tender_date: quote?.tender_date,
  tender_date_time_of_day: quote?.tender_date_time_of_day,
  earliest_tender: quote?.earliest_tender,
  cutoff_at: quote?.cutoff_at,
  dropoff_contact_id: quote?.dropoff_contact_id,
  provider_id: quote?.provider_id,
  inland_dropoff_contact_id: quote?.inland_dropoff_contact_id,
  from_dock_hours: quote?.from_dock_hours,
  to_dock_hours: quote?.to_dock_hours,
  pickup_ready_at: quote?.pickup_ready_at,
  dropoff_at: quote?.dropoff_at,
  emergency_contact_id: quote?.emergency_contact_id,
});

// takes an object with keys that may or may not have dot notations. It should return dot notations into a properly nested object
// e.g. { "delivery_address.city": "New York" } => { delivery_address: { city: "New York" } }
const convertDotNotationToObj = (obj: Record<any, string>) => {
  const newObj: Record<any, string> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const splitKey = key.split('.');

    if (splitKey.length > 1) {
      const [firstKey, ...rest] = splitKey;
      newObj[firstKey] = {
        ...(newObj[firstKey] as any),
        [rest.join('.')]: value,
      };
    } else {
      newObj[key] = value;
    }
  });
  return newObj;
};

export const BookingTemplate = ({ quote, ...rest }: BookingTemplateFormProps) => {
  const { data: request } = useRequest(quote.request_id);
  const { mutate: updateRequest } = useUpdateRequest(request?.id);
  const [updateQuote] = useUpdateQuote(quote?.id);

  const defaultValues = useMemo(() => mergeValues(request, { ...quote }), [request, quote]);

  const methods = useForm<Partial<Values>>({
    mode: 'onSubmit',
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const handleValid = async (fields: Record<any, string>) => {
    fields = convertDotNotationToObj(fields);

    // For updating quote fields, omit any fields from Request
    const quoteFields = omit(fields, REQUEST_KEYS);
    const hasQuoteFields = Object.keys(quoteFields)?.length > 0;

    // For updating request fields, omit any fields from Quote
    const requestFields = omit(fields, QUOTE_KEYS);
    const hasRequestFields = Object.keys(requestFields)?.length > 0;

    // Send updated fields to backend
    if (hasRequestFields) await updateRequest?.(requestFields);
    if (hasQuoteFields) {
      if (quoteFields['earliest_tender'] === '') {
        quoteFields['earliest_tender'] = null;
      } else if (quoteFields['tender_date_time_of_day'] === '') {
        quoteFields['tender_date_time_of_day'] = null;
      }
      await updateQuote?.(quoteFields);
    }
  };

  useEffect(() => {
    // TODO: make this more performant -> we should only conditionally reset the form whenever it changes.
    // resetting the form with default values / from the server / this probably won't actually work
    if (defaultValues) resetFields(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  useEffect(() => {
    if (quote && request) resetFields({ ...request, ...quote });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, request]);

  const resetFields = (values: any) => methods.reset(values);

  const isAfr = modelUtils.quote.isAfr(quote);
  const isExw = modelUtils.quote.isExw(quote);
  const isFca = modelUtils.quote.isFca(quote);

  return (
    <SimpleForm onValid={handleValid} defaultValues={defaultValues} {...methods}>
      {isAfr && <AfrBookingForm quote={quote} request={request} {...rest} />}
      {isExw && <ExwBookingForm quote={quote} {...rest} />}
      {isFca && <FcaBookingForm quote={quote} {...rest} />}
    </SimpleForm>
  );
};
